import React, { useState, useEffect } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useDispatch } from "react-redux";
import { searchMedicalConditions } from "../../../../../redux/modules/clinical-services/actions";
import { customReactSelectStyles } from "../../../clinical-services/services/select/medication-select";

export const ALERT_TYPE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "adherence-risk", label: "Adherence Risk" },
  { value: "on-anticoagulant", label: "Anticoagulation Therapy" },
  { value: "brand-drugs-only", label: "Brand Drugs Only" },
  { value: "cognitive-impairment", label: "Cognitive Impairment" },
  { value: "on-corticosteroids-chronic-use", label: "Corticosteroids (Chronic Use)" },
  { value: "high-fall-risk", label: "Fall Risk" },
  { value: "gastric-bypass", label: "Gastric Bypass" },
  { value: "gluten-free-diet", label: "Gluten Free Diet" },
  { value: "high-risk-medication", label: "High-Risk Medication" },
  { value: "immunocompromised", label: "Immunocompromised" },
  { value: "on-immunosuppressant-therapy", label: "Immunosuppressant Therapy" },
  { value: "kidney-transplant", label: "Kidney Transplant" },
  { value: "lactose-free-diet", label: "Lactose Free Diet" },
  { value: "language-barrier", label: "Language Barrier" },
  { value: "liver-transplant", label: "Liver Transplant" },
  { value: "lung-transplant", label: "Lung Transplant" },
  { value: "opioid-intolerance-sensitive", label: "Opioid Intolerance/ Sensitive" },
  { value: "on-opioid-therapy", label: "Opioid Therapy" },
  { value: "poly-pharmacy", label: "Poly Pharmacy" },
  { value: "peanut-allergy-alert", label: "Peanut Allergy Alert" },
  { value: "qt-prolongation-risk-multiple-meds", label: "QT Prolongation Risk (Multiple Meds)" },
  { value: "smoking", label: "Smoking" },
  { value: "other", label: "Other" }
];

const ClinicalAlertTypeSelect = ({
  onChange = () => {},
  value = null,
  label = "Clinical Alert Type",
  isRequired = true,
  error = false,
  className = "",
}) => {
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState(null);
  const [showError, setShowError] = useState(false);

  // Combine predefined alert types with medical conditions
  const loadOptions = async (inputValue) => {
    // Filter predefined alert types
    const filteredAlertTypes = inputValue
      ? ALERT_TYPE_OPTIONS.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        )
      : ALERT_TYPE_OPTIONS;

    // If no input, return only predefined alert types
    if (!inputValue) {
      return filteredAlertTypes;
    }

    // Fetch medical conditions from backend
    let medicalConditions = [];
    try {
      const results = await dispatch(searchMedicalConditions(inputValue));
      if (results && results.length > 0) {
        medicalConditions = results.map((item) => ({
          value: item.name || item.id,
          label: item.name,
          isMedicalCondition: true,
        }));
      }
    } catch (error) {
      console.error("Error fetching medical conditions:", error);
    }

    // Combine results, ensuring no duplicates
    const combinedOptions = [
      ...filteredAlertTypes,
      ...medicalConditions.filter(
        (condition) =>
          !filteredAlertTypes.some(
            (alertType) => alertType.label === condition.label
          )
      ),
    ];

    return combinedOptions;
  };

  const handleCreate = (inputValue) => {
    const newOption = {
      label: inputValue,
      value: inputValue,
    };
    setSelectedOption(newOption);
    setShowError(isRequired && !newOption.value);
    onChange(newOption);
  };

  const handleChange = (selectedOption) => {
    setSelectedOption(selectedOption);
    setShowError(isRequired && !selectedOption);
    onChange(selectedOption);
  };

  useEffect(() => {
    if (value?.value) {
      setSelectedOption(value);
    } else {
      setSelectedOption(null);
    }
  }, [value]);

  useEffect(() => {
    setShowError(error);
  }, [error]);

  return (
    <div className={className || "w-full col-span-12"}>
      <div
        className={`relative rounded-[4px] border-[1px] ${
          isRequired && selectedOption === null ? "bg-blue-100" : "bg-white"
        } ${showError ? "border-red-200" : "border-gray-100"} px-3 py-3 focus-within:border-REELBlue focus-within:border-[2px]`}
      >
        <div>
          <span className="block mb-1 text-gray-900 text-body-xs">
            {label} {isRequired && <span className="text-red-200">*</span>}
          </span>
          <AsyncCreatableSelect
            loadOptions={loadOptions}
            defaultOptions={true}
            cacheOptions
            styles={customReactSelectStyles(isRequired, selectedOption)}
            value={selectedOption}
            onChange={handleChange}
            onCreateOption={handleCreate}
            placeholder={`Enter or Select ${label}`}
            noOptionsMessage={() => "No alert types found"}
            isSearchable={true}
            isClearable={true}
            formatOptionLabel={({ label }) => label}
          />
        </div>
      </div>
      {showError && isRequired && (
        <span className="text-red-200 text-xs">{label} is required</span>
      )}
    </div>
  );
};

export default ClinicalAlertTypeSelect;

